/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-04
 */

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  IconButton,
  InputAdornment,
  Divider,
  CircularProgress,
  Link,
} from '@mui/material';
import { ArrowBack, Visibility, VisibilityOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { useSessionLoginMutation } from '../../../features/auth';
import { invitationApi } from '../../../api/invitationApi';
import {
  InvitationDetail,
  InvitationResponse,
} from '../../../types/invitation';

interface ExistingUserInvitationFormProps {
  invitation: InvitationDetail;
  token: string;
  onSuccess: (
    response: InvitationResponse,
    isNewUser?: boolean,
    userEmail?: string
  ) => void;
  onError: (error: string) => void;
  onBack: () => void;
  loading?: boolean;
}

/**
 * Form component for existing users to accept invitations.
 * Only requires password confirmation since the user already exists.
 */
const ExistingUserInvitationForm: React.FC<ExistingUserInvitationFormProps> = ({
  invitation,
  token,
  onSuccess,
  onError: _onError,
  onBack,
  loading = false,
}) => {
  const { t } = useTranslation('studio');
  const [sessionLogin] = useSessionLoginMutation();

  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState<string>('');

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
    // Clear error when user starts typing
    if (loginError) {
      setLoginError('');
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoginError('');
    setIsSubmitting(true);

    if (!password.trim()) {
      setLoginError(t('invitationForms.existing.errors.passwordRequired'));
      setIsSubmitting(false);
      return;
    }

    try {
      // First, authenticate the user
      await sessionLogin({
        email: invitation.email,
        password,
      }).unwrap();

      // If login successful, accept the invitation
      const response = await invitationApi.acceptInvitation(token, {});
      onSuccess(response, false); // Pass isNewUser=false for existing users
    } catch (error: any) {
      // Handle authentication errors
      if (error.data?.detail) {
        const errorMessage = error.data.detail;
        if (
          errorMessage.toLowerCase().includes('invalid') ||
          errorMessage.toLowerCase().includes('incorrect') ||
          errorMessage.toLowerCase().includes('password')
        ) {
          setLoginError(
            t('invitationForms.existing.errors.invalidCredentials')
          );
        } else {
          setLoginError(errorMessage);
        }
      } else if (error.data?.non_field_errors) {
        setLoginError(error.data.non_field_errors[0]);
      } else if (error.message) {
        setLoginError(error.message);
      } else {
        setLoginError(t('invitationForms.existing.errors.authFailed'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={onBack} size="small" disabled={isSubmitting}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" component="h2">
            {t('invitationForms.existing.title')}
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {t('invitationForms.existing.alert', {
              email: invitation.email,
              studioName: invitation.studio_name,
              role: invitation.role_display,
            })}
          </Typography>
        </Alert>

        <Divider sx={{ my: 3 }} />

        <Box component="form" onSubmit={handleSubmit}>
          {loginError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {loginError}
            </Alert>
          )}

          <TextField
            fullWidth
            label={t('invitationForms.existing.emailLabel')}
            value={invitation.email}
            disabled
            sx={{ mb: 3 }}
            helperText={t('invitationForms.existing.emailHelper')}
          />

          <TextField
            fullWidth
            label={t('invitationForms.existing.passwordLabel')}
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={handlePasswordChange}
            disabled={isSubmitting || loading}
            required
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                    disabled={isSubmitting || loading}
                    aria-label={t('invitationForms.existing.togglePassword')}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Link
              href="/forgot-password"
              style={{
                textDecoration: 'none',
                color: 'inherit',
                marginBottom: '16px',
                display: 'block',
              }}
            >
              {t('invitationForms.existing.forgotPassword')}
            </Link>
          </Box>

          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={onBack}
              disabled={isSubmitting || loading}
            >
              {t('invitationForms.existing.backButton')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || loading || !password.trim()}
              sx={{ minWidth: 140 }}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  {t('invitationForms.existing.submitting')}
                </>
              ) : (
                t('invitationForms.existing.submitButton')
              )}
            </Button>
          </Box>
        </Box>

        <Box mt={3}>
          <Typography variant="body2" color="text.secondary">
            {t('invitationForms.existing.footer', {
              studioName: invitation.studio_name,
              role: invitation.role_display,
            })}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ExistingUserInvitationForm;
